import { ReportTemplate } from '../../models/reportTemplate.model';
import { TemplateSupportedFilter } from '../../models/template.model';
import { Sample } from '../../models/sample.model';
import { IhLabResult } from '../../models/IhLabResult.model';
import { SampleTypeEnum } from '../../enums/sampleType.enum';
import { SampleCategoryEnum } from '../../enums/sampleCategory.enum';
import { SampleStatusEnum } from '../../enums/sampleStatus.enum';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { EMPTY_GUID } from 'src/app/_helpers/guid.helper';
import { BaseTemplateMapper, FilterOption, FilterValue } from './base-template-mapper';
import { DateFormatService } from '../dateFormat.service';
import { TranslateService } from '../translate.service';
import { MultiSampleTablePresentationService, MultiSampleTablePresentation, MultiSampleTablePresentationOptions } from '../../components/template-widgets/sample-widgets/multi-sample-table-widget/multi-sample-table-presentation.service';
import { MultiSampleData } from '../../components/template-widgets/sample-widgets/multi-sample-table-widget/multi-sample-table-widget.component';

export const FILTER_KEYS = {
    sampleNumber: nameOf<Sample>(x => x.sampleNumber),
    sampleStatus: nameOf<Sample>(x => x.sampleStatus),
    sampleCategory: nameOf<Sample>(x => x.sampleCategory),
    agent: nameOf<IhLabResult>(x => x.agentId),
    jurisdiction: nameOf<IhLabResult>(x => x.jurisdictionId),
    oelType: nameOf<IhLabResult>(x => x.oelTypeId),
    segId: nameOf<Sample>(x => x.segId)
};

export const UNASSIGNED_SEG_VALUE = 'unassigned-seg';
const UNASSIGNED_SEG_LABEL = 'Unknown SEG';

export const DEFAULT_EXCLUDED_SAMPLE_STATUSES: SampleStatusEnum[] = [
    SampleStatusEnum.Void,
    SampleStatusEnum.VoidLetterSent
];

interface FilterDescriptor {
    key: string;
    scope: 'sample' | 'labResult';
    buildValues: (samples: Sample[], allLabResults: IhLabResult[]) => FilterValue[];
    getDefaultSelection?: (values: FilterValue[]) => (number | string)[];
    defaultLabel?: () => string;
    matchesSample?: (sample: Sample, selected: (number | string)[]) => boolean;
    matchesLabResult?: (labResult: IhLabResult, sample: Sample, selected: (number | string)[]) => boolean;
}

export abstract class SampleBasedTemplateMapper<T = unknown> extends BaseTemplateMapper<T> {
    constructor(
        protected dateFormatService: DateFormatService,
        protected translateService: TranslateService,
        protected multiSampleTablePresentationService?: MultiSampleTablePresentationService
    ) { super(); }

    private readonly filterDescriptors: FilterDescriptor[] = [
        {
            key: FILTER_KEYS.sampleNumber,
            scope: 'sample',
            buildValues: samples => this.buildSampleNumberFilterValues(samples),
            defaultLabel: () => this.translateService.getDynamicTranslation('Sample Numbers'),
            matchesSample: (sample, selected) => selected.includes(sample.id)
        },
        {
            key: FILTER_KEYS.sampleStatus,
            scope: 'sample',
            buildValues: samples => this.buildSampleStatusFilterValues(samples),
            getDefaultSelection: values => this.getDefaultSampleStatusSelection(values),
            defaultLabel: () => this.translateService.getDynamicTranslation('Sample Status'),
            matchesSample: (sample, selected) => selected.includes(sample.sampleStatus)
        },
        {
            key: FILTER_KEYS.sampleCategory,
            scope: 'sample',
            buildValues: samples => this.buildSampleCategoryFilterValues(samples),
            defaultLabel: () => this.translateService.getDynamicTranslation('Sample Category'),
            matchesSample: (sample, selected) => selected.includes(sample.sampleCategory)
        },
        {
            key: FILTER_KEYS.agent,
            scope: 'labResult',
            buildValues: (_samples, allLabResults) => this.buildFilterValues(allLabResults, r => r.agentId, r => r.agentName || `Agent ${r.agentId}`)
                .sort((a, b) => a.label.localeCompare(b.label)),
            matchesLabResult: (labResult, _sample, selected) => selected.includes(labResult.agentId)
        },
        {
            key: FILTER_KEYS.jurisdiction,
            scope: 'labResult',
            buildValues: (_samples, allLabResults) => this.buildFilterValues(allLabResults, r => r.jurisdictionId, r => r.jurisdictionName || `Jurisdiction ${r.jurisdictionId}`),
            matchesLabResult: (labResult, _sample, selected) => selected.includes(labResult.jurisdictionId)
        },
        {
            key: FILTER_KEYS.oelType,
            scope: 'labResult',
            buildValues: samples => this.buildOelTypeFilterValues(samples),
            matchesLabResult: (labResult, sample, selected) => {
                const adjustedKey = this.buildAdjustedOelKey(sample);
                return (labResult.oelTypeId != null && selected.includes(labResult.oelTypeId))
                    || (!!adjustedKey && labResult.isOelCorrectionApplied && selected.includes(adjustedKey));
            }
        },
        {
            key: FILTER_KEYS.segId,
            scope: 'sample',
            buildValues: samples => this.buildFilterValues(samples, s => this.toSegFilterValue(s), s => s.segName || this.translateService.getDynamicTranslation(UNASSIGNED_SEG_LABEL)),
            defaultLabel: () => this.translateService.getDynamicTranslation('SEG'),
            matchesSample: (sample, selected) => selected.includes(this.toSegFilterValue(sample))
        }
    ];

    protected abstract getSamples(entity: T): Sample[];

    protected formatDate(date: Date | string | number | undefined, fallback: string = ''): string {
        if (!date) return fallback;
        if (typeof date === 'string') {
            return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(date, this.dateFormatService.userDateFormat);
        }
        if (typeof date === 'number') {
            return this.dateFormatService.formatUtcTimestampToUserTimezone(date, this.dateFormatService.userDateFormat);
        }
        if (date instanceof Date) {
            return this.dateFormatService.formatDateTimeWithTimeZoneToString(date, this.dateFormatService.userDateFormat);
        }
        return fallback;
    }

    analyzeFilters(entity: T, template?: ReportTemplate): FilterOption[] {
        const samples = this.getSamples(entity);
        const allLabResults = samples?.flatMap(s => s.ihLabResults || []) || [];
        const supportedFilters = template?.templateJson?.supportedFilters;
        const filters: FilterOption[] = [];
        if (!supportedFilters?.length) return filters;

        for (const descriptor of this.orderDescriptorsForDisplay(supportedFilters)) {
            const cfg = supportedFilters.find(c => this.extractFilterPath(c.path) === descriptor.key);
            if (!cfg) continue;
            if (descriptor.scope === 'sample' && !samples?.length) continue;
            if (descriptor.scope === 'labResult' && !allLabResults.length) continue;

            const values = descriptor.buildValues(samples ?? [], allLabResults);
            if (!values.length) continue;

            filters.push({
                path: descriptor.key,
                label: cfg.ui?.label || (descriptor.defaultLabel ? descriptor.defaultLabel() : descriptor.key),
                availableValues: values,
                selectedValues: descriptor.getDefaultSelection ? descriptor.getDefaultSelection(values) : values.map(v => v.value)
            });
        }

        return filters;
    }

    private orderDescriptorsForDisplay(supportedFilters: TemplateSupportedFilter[]): FilterDescriptor[] {
        const labResultDescriptors = supportedFilters
            .map(cfg => this.filterDescriptors.find(d => d.scope === 'labResult' && d.key === this.extractFilterPath(cfg.path)))
            .filter((descriptor): descriptor is FilterDescriptor => !!descriptor);

        return [...this.filterDescriptors.filter(d => d.scope === 'sample'), ...labResultDescriptors];
    }

    private toSegFilterValue(sample: Sample): string {
        const segId = sample.segId;
        if (!segId || segId === EMPTY_GUID || !sample.segName?.trim()) {
            return UNASSIGNED_SEG_VALUE;
        }
        return segId;
    }

    private buildAdjustedOelKey(sample: Sample): string | null {
        const hours = sample.shiftLength ? Math.round(sample.shiftLength / 3600) : 0;
        return hours > 0 ? `adjusted_${hours}hr` : null;
    }

    applyFilters(entity: T, filters: Record<string, (number | string)[]>): unknown[] {
        const samples = this.getSamples(entity);
        if (!samples) return [];

        let filteredSamples = samples;
        for (const descriptor of this.filterDescriptors) {
            if (descriptor.scope !== 'sample' || !descriptor.matchesSample) continue;
            const selected = filters[descriptor.key];
            if (selected?.length) {
                filteredSamples = filteredSamples.filter(s => descriptor.matchesSample!(s, selected));
            }
        }

        const labResultDescriptors = this.filterDescriptors.filter(d => d.scope === 'labResult' && d.matchesLabResult);
        const hasResultFilters = labResultDescriptors.some(d => filters[d.key]?.length);
        if (!hasResultFilters) return filteredSamples;

        return filteredSamples.map(s => {
            const filteredResults = (s.ihLabResults || []).filter(r =>
                labResultDescriptors.every(d => {
                    const selected = filters[d.key];
                    return !selected?.length || d.matchesLabResult!(r, s, selected);
                })
            );
            return { ...s, ihLabResults: filteredResults };
        });
    }

    override updateCascadingFilters(entity: T, filters: FilterOption[], changedFilterPath: string): void {
        const samples = this.getSamples(entity);
        if (changedFilterPath !== FILTER_KEYS.oelType || !samples) return;

        const oelFilter = filters.find(f => f.path === FILTER_KEYS.oelType);
        if (!oelFilter) return;

        const selected = oelFilter.selectedValues;
        const visibleAgents = new Set<string>(), visibleJurisdictions = new Set<string>();

        for (const sample of samples) {
            const adjKey = this.buildAdjustedOelKey(sample);

            for (const lr of sample.ihLabResults || []) {
                if ((lr.oelTypeId && selected.includes(lr.oelTypeId)) || (adjKey && lr.isOelCorrectionApplied && selected.includes(adjKey))) {
                    visibleAgents.add(lr.agentId);
                    if (lr.jurisdictionId) visibleJurisdictions.add(lr.jurisdictionId);
                }
            }
        }

        const updateFilter = (filter: FilterOption | undefined, visibleSet: Set<string>) => {
            if (!filter) return;
            for (const opt of filter.availableValues) {
                const visible = typeof opt.value === 'string' && visibleSet.has(opt.value), wasDisabled = opt.disabled === true;
                opt.disabled = !visible;
                const idx = filter.selectedValues.indexOf(opt.value);
                if (!visible && idx > -1) filter.selectedValues.splice(idx, 1);
                else if (wasDisabled && visible && idx === -1) filter.selectedValues.push(opt.value);
            }
        };

        updateFilter(filters.find(f => f.path === FILTER_KEYS.agent), visibleAgents);
        updateFilter(filters.find(f => f.path === FILTER_KEYS.jurisdiction), visibleJurisdictions);
    }

    protected buildSampleLabel(sample: Sample): string {
        const sampleNumber = sample.sampleNumber || `Sample ${sample.id}`;
        const sampleType = sample.sampleType != null ? getNamedEnum(SampleTypeEnum)[sample.sampleType] : '';
        const sampleCategory = sample.sampleCategory != null ? getNamedEnum(SampleCategoryEnum)[sample.sampleCategory] : '';

        if (sampleType && sampleCategory) return `${sampleNumber} - ${sampleType} - ${sampleCategory}`;
        if (sampleType) return `${sampleNumber} - ${sampleType}`;
        if (sampleCategory) return `${sampleNumber} - ${sampleCategory}`;
        return sampleNumber;
    }

    protected buildSampleNumberFilterValues(samples: Sample[]): FilterValue[] {
        return samples.map(sample => ({
            value: sample.id,
            label: this.buildSampleLabel(sample),
            count: sample.ihLabResults?.length || 0
        }));
    }

    private buildEnumFieldFilterValues(
        samples: Sample[],
        getField: (sample: Sample) => number | null | undefined,
        enumNames: Record<number, string>,
        fallbackPrefix: string
    ): FilterValue[] {
        const counts = new Map<number, number>();
        for (const sample of samples) {
            const value = getField(sample);
            if (value == null) continue;
            counts.set(value, (counts.get(value) || 0) + 1);
        }
        return Array.from(counts.entries())
            .map(([value, count]) => ({ value, label: enumNames[value] || `${fallbackPrefix} ${value}`, count }))
            .sort((a, b) => (a.value as number) - (b.value as number));
    }

    protected buildSampleStatusFilterValues(samples: Sample[]): FilterValue[] {
        return this.buildEnumFieldFilterValues(samples, s => s.sampleStatus, getNamedEnum(SampleStatusEnum), 'Status');
    }

    protected buildSampleCategoryFilterValues(samples: Sample[]): FilterValue[] {
        return this.buildEnumFieldFilterValues(samples, s => s.sampleCategory, getNamedEnum(SampleCategoryEnum), 'Category');
    }

    protected resolveSampleEnumLabels(sample: Sample): Record<string, unknown> {
        const statusNames = getNamedEnum(SampleStatusEnum);
        const categoryNames = getNamedEnum(SampleCategoryEnum);
        return {
            [FILTER_KEYS.sampleStatus]: sample.sampleStatus != null ? (statusNames[sample.sampleStatus] ?? sample.sampleStatus) : sample.sampleStatus,
            [FILTER_KEYS.sampleCategory]: sample.sampleCategory != null ? (categoryNames[sample.sampleCategory] ?? sample.sampleCategory) : sample.sampleCategory
        };
    }

    protected withResolvedSampleEnumLabels(sample: Sample): Record<string, unknown> {
        return this.createDynamicProxy(sample, this.resolveSampleEnumLabels(sample));
    }

    protected getDefaultSampleStatusSelection(statusValues: FilterValue[]): (number | string)[] {
        const selected = statusValues
            .filter(v => !DEFAULT_EXCLUDED_SAMPLE_STATUSES.includes(v.value as SampleStatusEnum))
            .map(v => v.value);
        return selected.length ? selected : statusValues.map(v => v.value);
    }

    protected buildOelTypeFilterValues(samples: Sample[]): FilterValue[] {
        const oelTypes = new Map<string, { name: string; count: number }>();
        const adjusted = new Map<string, number>();

        for (const sample of samples) {
            const adjustedKey = this.buildAdjustedOelKey(sample);
            for (const r of sample.ihLabResults || []) {
                if (r.oelTypeName && r.oelTypeId) {
                    const e = oelTypes.get(r.oelTypeId);
                    if (e) e.count++;
                    else oelTypes.set(r.oelTypeId, { name: r.oelTypeName, count: 1 });
                }
                if (r.isOelCorrectionApplied && r.correctedOEL && adjustedKey) {
                    adjusted.set(adjustedKey, (adjusted.get(adjustedKey) || 0) + 1);
                }
            }
        }

        return [
            ...Array.from(oelTypes.entries()).map(([id, d]) => ({ value: id, label: d.name, count: d.count })),
            ...Array.from(adjusted.entries()).map(([key, count]) => ({ value: key, label: `${key.match(/\d+/)?.[0]}-Hr Adjusted OEL`, count }))
        ];
    }

    buildMultiSampleTableContext(
        multiSampleData: MultiSampleData | null | undefined,
        options: MultiSampleTablePresentationOptions
    ): MultiSampleTablePresentation | null {
        if (!this.multiSampleTablePresentationService) return null;
        return this.multiSampleTablePresentationService.build(
            multiSampleData,
            options,
            this.buildMultiSampleTableLabels()
        );
    }

    private buildMultiSampleTableLabels() {
        const t = (key: string) => this.translateService.getDynamicTranslation(key);
        const oelLines = t('Occupational<br>Exposure<br>Limits [mg/m³]').split('<br>');
        return {
            concentrationsHeader: t('All Concentrations shown with individual units'),
            sampleNumber: t('Sample Number'),
            jobTitleArea: t('Job Title/Area'),
            oelHeaderLine1: oelLines[0] ?? '',
            oelHeaderLine2: oelLines[1] ?? '',
            oelHeaderLine3: oelLines[2] ?? '',
            sampleType: t('Sample Type'),
            respType: t('Resp. Type'),
            respApf: t('Resp. APF'),
            mucExceeded: t('MUC Exceeded'),
            startDate: t('Start Date'),
            startTime: t('Start Time'),
            durationMin: t('Duration (min)'),
            volumeLiters: t('Volume (Liters)'),
            taskArea: t('Task/Area'),
            noTaskDescription: t('No task description available')
        };
    }

}
